import React, { useRef, useState } from 'react';
import { Box, Typography, IconButton, Button } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { IContest } from '../../types/global/helper';
import { RectangularImageCropperModal } from '../ImageCropper/RectangularImageCropperModal';
import { RICH_MEDIA_SHARE_ASPECT } from '../../constants/richMediaShareImage';
import { RichMediaShareSizeHint } from '../Common/RichMediaShareSizeHint';
import { PhotoCamera } from '@mui/icons-material';

interface ContestCoverImageProps {
    contest: IContest;
    editMode?: boolean;
    onImageChange?: (image: string, rawFile: File) => void;
}

export const ContestCoverImage: React.FC<ContestCoverImageProps> = ({ contest, editMode = false, onImageChange }) => {
    const { themeStyle } = useThemeContext();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [src, setSrc] = useState<string>('');
    const [pendingImage, setPendingImage] = useState<string | null>(null);
    const [pendingFile, setPendingFile] = useState<File | null>(null);

    const handleUploadClick = () => {
        if (fileInputRef.current) fileInputRef.current.click();
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            const file = files[0];
            setSrc(URL.createObjectURL(file));
            setModalOpen(true);
        }
    };

    // After cropping, show preview and buttons, but do not call onImageChange yet
    const handleCropSave = (base64: string, file: File) => {
        setModalOpen(false);
        setSrc('');
        setPendingImage(base64);
        setPendingFile(file);
    };

    const handleCropCancel = () => {
        setModalOpen(false);
        setSrc('');
    };

    // User clicks Cancel on preview overlay
    const handlePendingCancel = () => {
        setPendingImage(null);
        setPendingFile(null);
    };

    // User clicks Save on preview overlay
    const handlePendingSave = () => {
        if (pendingImage && pendingFile && onImageChange) {
            onImageChange(URL.createObjectURL(pendingFile), pendingFile);
        }
        setPendingImage(null);
        setPendingFile(null);
    };

    // Determine which image to show
    const displayImage = pendingImage || contest?.feature_image;
    const isApproved = contest.request?.request_status === 'approved';

    return (
        <Box
            sx={{
                position: 'relative',
                width: '100%',
                height: { xs: '200px', sm: '300px' },
                borderRadius: 2,
                overflow: 'hidden',
                mb: 2,
                backgroundColor: displayImage ? 'transparent' : undefined,
            }}>
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundImage: displayImage ? `url(${displayImage})` : undefined,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    filter: 'brightness(0.7)',
                    zIndex: 1,
                }}
            />
            {
                <Typography
                    style={{
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        backgroundColor: isApproved
                            ? theme[themeStyle].switchGreen
                            : theme[themeStyle].red_status_indicator,
                        padding: 5,
                        borderRadius: 2,
                        position: 'absolute',
                        top: 10,
                        left: 10,
                        zIndex: 1,
                        color: isApproved ? theme[themeStyle].true_black : theme[themeStyle].true_white,
                        fontSize: 12,
                    }}>
                    {isApproved ? 'Approved' : 'Pending Approval '}
                </Typography>
            }
            {/* Upload Icon Overlay */}
            {editMode && !pendingImage && (
                <>
                    <IconButton
                        sx={{
                            position: 'absolute',
                            top: 15,
                            right: 15,
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            color: theme[themeStyle].primary_white,
                            zIndex: 10,
                            '&:hover': { backgroundColor: theme[themeStyle].primary_orange },
                        }}
                        onClick={handleUploadClick}
                        aria-label="Upload Cover Image">
                        <PhotoCamera sx={{ color: theme[themeStyle].true_white }} />
                        <input
                            type="file"
                            accept="image/*"
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                            onChange={handleFileChange}
                        />
                    </IconButton>
                    <RichMediaShareSizeHint
                        sx={{
                            position: 'absolute',
                            top: 56,
                            right: 15,
                            zIndex: 10,
                            color: theme[themeStyle].primary_white,
                            opacity: 0.75,
                            fontSize: 10,
                            mt: 0,
                            textAlign: 'right',
                            maxWidth: 140,
                            lineHeight: 1.3,
                        }}
                    />
                </>
            )}
            {/* Reusable Cropper Modal */}
            <RectangularImageCropperModal
                open={modalOpen}
                src={src}
                onSave={handleCropSave}
                onCancel={handleCropCancel}
                aspect={RICH_MEDIA_SHARE_ASPECT}
                cropHeight={200}
                variant="richMediaShare"
            />
            {/* Pending image preview overlay with Save/Cancel */}
            {editMode && pendingImage && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        zIndex: 20,
                        background: 'rgba(0,0,0,0.25)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                    <img
                        src={pendingImage}
                        alt="Preview"
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: 8,
                            boxShadow: '0 2px 16px rgba(0,0,0,0.2)',
                        }}
                    />
                    <Box sx={{ position: 'absolute', top: 20, right: 20, display: 'flex', gap: 2 }}>
                        <Button variant="outlined" color="inherit" onClick={handlePendingCancel} sx={{ mr: 1 }}>
                            Cancel
                        </Button>
                        <Button variant="contained" color="primary" onClick={handlePendingSave}>
                            Save
                        </Button>
                    </Box>
                </Box>
            )}
            <Box
                sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: '100%',
                    padding: 3,
                    background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)',
                    zIndex: 2,
                }}>
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: { xs: '1.5rem', sm: '2rem' },
                        color: theme[themeStyle].true_white,
                    }}>
                    {contest.title}
                </Typography>

                {editMode && (
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                            color: theme[themeStyle].true_white,
                            opacity: 0.8,
                            mt: 1,
                        }}>
                        Manage Contest Details
                    </Typography>
                )}
            </Box>
        </Box>
    );
};
